import { SLIDER_UPDATE_MINUTES, moodLabel, signedFigure, type MoodCheckin } from '@adhd/shared';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { CrisisNoticeCard, type CrisisNotice } from './CrisisNotice.js';
import { SLIDER_KEYS } from './RatingSlider.js';

/**
 * The slider, always, in the middle of Today.
 *
 * Release 1 of 2.0 (docs/PRD-2.0.md §2): dragging it is a check-in and nothing
 * else. It records on RELEASE — pointer up, or a key that moved it coming up —
 * never on every input event, so a drag is one row and not forty. Nick's words
 * on 12 October were that the simplicity of telling his mood is a thing he
 * likes about the app; this keeps that and removes the Save button in between.
 *
 * It does not replace the form. "Add a note" opens the full check-in below it,
 * already open, for the days a sentence is the point (31 of his 77 check-ins
 * carry one). A note added there is its own check-in, as it always was.
 *
 * NULL UNTIL TOUCHED, for the reason CheckIn.tsx gives at length: a figure
 * nobody gave must never be written. The thumb parks at zero and the word reads
 * "Not said yet" until it is moved. After a save the slider shows what was
 * saved and says when; moving it again is another check-in, which is the
 * intended way to say the day has moved.
 */
export function RateNow({
  last,
  day,
  onSaved,
  onMore,
}: {
  /** The day's latest check-in, so the slider can sit where the day is. */
  last: MoodCheckin | null;
  /** The day Today is showing — before 4am, yesterday (14 Oct 2026). */
  day?: string;
  onSaved: () => void;
  /** Opens the full form with the note, below. */
  onMore: () => void;
}): JSX.Element {
  const [value, setValue] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<MoodCheckin | null>(null);
  const [crisis, setCrisis] = useState<CrisisNotice | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * ONE STATEMENT, REVISED — not a row per touch (Nick, 13 Oct: four check-ins
   * in ten seconds from bumping the thumb). Three rules:
   *
   *   1. A release that leaves the value where it was saves nothing. Touching
   *      the track without moving is not a check-in.
   *   2. A release within SLIDER_UPDATE_MINUTES of the latest check-in
   *      updates that check-in (PATCH) instead of adding one. "drag to
   *      update" under the slider has always said so; now it is true.
   *   3. The save waits a moment after the last release, so a grab-and-regrab
   *      is one request, not two.
   */
  const current = saved ?? last;
  const shown = value ?? current?.mood ?? null;
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);

  function release(v: number): void {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { timer.current = null; void commit(v); }, SETTLE_MS);
  }

  /*
   * The same word, again (14 Oct 2026, his question). Releasing the slider
   * where it already sits saves nothing — that is what stopped a touch making
   * four check-ins — but the touch still means "check in", so the slider
   * answers it: once the latest check-in is past the revision window, a
   * release at the same value ASKS, "Log Neutral again? Yes · No", and Yes is
   * one tap. Inside the window a touch stays silent: it would be the same
   * check-in. A question, never a write, so a wobble costs nothing.
   */
  const [askAgain, setAskAgain] = useState(false);
  /*
   * THE DAY'S LATEST WINS (20 Oct 2026). `saved` is this slider's own copy of
   * what it last wrote, there to cover the beat before Today's refetch lands —
   * and it was preferred over `last` for as long as the screen stayed mounted.
   * So a check-in the door filed, one made on another device, one deleted from
   * the list, or a new day at 4am all reached the word at the top of Today and
   * never reached the slider under it, which went on showing the old score.
   * Whenever the day's latest check-in stops being the one the slider saved,
   * the copy is dropped and the slider shows what the record says. A new day
   * is that case too: Today hands it the new day's latest, or nothing. While
   * a refetch brings back the same check-in the copy stays, so "saved ·
   * 10:10 PM" is still the confirmation a drag gets.
   */
  useEffect(() => {
    setSaved((s) => (s && last && s.id === last.id && s.mood === last.mood ? s : null));
    setAskAgain(false);
  }, [last?.id, last?.mood, last?.recordedAt]);
  async function again(): Promise<void> {
    if (busy || current === null) return;
    setBusy(true);
    setError(null);
    try {
      const row = await api.createCheckin({ mood: current.mood, source: 'manual', ...(day ? { day } : {}) });
      setSaved(row);
      setAskAgain(false);
      if (row.crisis) setCrisis(row.crisis);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  async function commit(v: number): Promise<void> {
    if (busy) return;
    const base = saved ?? last;
    if (base && base.mood === v) {
      setValue(null);
      if (Date.now() - Date.parse(base.recordedAt) >= SLIDER_UPDATE_MINUTES * 60_000) setAskAgain(true);
      return;
    }
    setAskAgain(false);
    setBusy(true);
    setError(null);
    try {
      const recent = base !== null && Date.now() - Date.parse(base.recordedAt) < SLIDER_UPDATE_MINUTES * 60_000;
      if (recent) {
        const row = await api.updateCheckin(base.id, { mood: v });
        setSaved(row);
      } else {
        const row = await api.createCheckin({ mood: v, source: 'manual', ...(day ? { day } : {}) });
        setSaved(row);
        if (row.crisis) setCrisis(row.crisis);
      }
      setValue(null);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rate stack-sm stack" aria-label="Right now">
      <div className="rate-lab">
        <span className="rate-k">Right now</span>
        <strong className={`rate-word${shown === null ? ' muted' : ''}`}>
          {shown === null ? 'Not said yet' : `${moodLabel(shown)} ${signedFigure(shown)}`}
        </strong>
      </div>
      <label className="sr-only" htmlFor="rate-now">
        Overall right now, from awful to great
      </label>
      <input
        id="rate-now"
        className={`slider rate-slider${shown === null ? ' slider-unset' : ''}`}
        type="range"
        min={-5}
        max={5}
        step={1}
        value={shown ?? 0}
        disabled={busy}
        aria-valuetext={shown === null ? 'not said yet' : `${moodLabel(shown)} ${signedFigure(shown)}`}
        onChange={(e) => setValue(Number(e.target.value))}
        onPointerUp={(e) => release(Number(e.currentTarget.value))}
        onKeyUp={(e) => {
          if (SLIDER_KEYS.has(e.key)) release(Number(e.currentTarget.value));
        }}
      />
      <div className="rate-ends">
        <span>awful</span>
        <span className="rate-when">
          {busy
            ? 'saving…'
            : saved
              ? `saved · ${clock(saved.recordedAt)}`
              : last
                ? `${clock(last.recordedAt)} · drag to update`
                : 'drag to say how it is'}
        </span>
        <span>great</span>
      </div>
      {error && (
        <p className="faint" role="alert">
          {error}
        </p>
      )}
      {crisis && <CrisisNoticeCard notice={crisis} onDismiss={() => setCrisis(null)} />}
      {askAgain && current && !busy && (
        <p className="muted" role="status" style={{ margin: 0 }}>
          Log {moodLabel(current.mood)} again?{' '}
          <button type="button" className="linkish" onClick={() => void again()}>Yes</button>
          {' · '}
          <button type="button" className="linkish linkish-quiet" onClick={() => setAskAgain(false)}>No</button>
        </p>
      )}
      <button type="button" className="linkish linkish-quiet rate-more" onClick={onMore}>
        Add a note, or more detail
      </button>
    </section>
  );
}

/** How long a release waits before saving, so a regrab is one save. */
const SETTLE_MS = 600;

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
